import { action, observable } from "mobx";
import { entity, IModelAttributes, Model } from "Models/Model";
import SuperAdministratorScheme from "Models/Security/Acl/SuperAdministratorScheme";
import { IAcl } from "Models/Security/IAcl";
import moment from "moment";
import { IMonitoredEntityAttributes } from ".";
import MonitoredEntity from "./MonitoredEntity";
import { EntityNameType } from "Models/Enums";

export interface IBusinessDetailEntityAttributes extends IModelAttributes {

	// abn fields
	abnActive: boolean;
	abnActiveSince: Date | null;
	abnLastUpdated: Date | null;
	abrLastSynced: Date | null;
	abnLocality: string | null;
	abnName: string | null;
	abnRegisteredForGstFrom: Date | null;
	abnStatus: string | null;
	abnTypeCode: string | null;

	// acn fields
	acnClass: string | null;
	acnName: string | null;
	acnRegistrationDate: Date | null;
	acnStatus: string | null;
	acnSubClass: string | null;
	acnTypeCode: string | null;
	dataGovLastSynced: Date | null;

	entityNames: EntityName[] | null;

	// references
	monitoredId: string | null;
	monitored: MonitoredEntity | IMonitoredEntityAttributes | null;

}

interface EntityName {
	text: string
	type: EntityNameType 
}

@entity('BusinessDetailEntity', 'BusinessDetail')
export default class BusinessDetailEntity extends Model
	implements IBusinessDetailEntityAttributes {

	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
	];

	// ABN fields
	@observable abnActive: boolean = false;
	@observable abnActiveSince: Date | null = null;
	@observable abnLastUpdated: Date | null = null;
	@observable abrLastSynced: Date | null = null;
	@observable abnLocality: string | null = null;
	@observable abnName: string | null = null;
	@observable abnRegisteredForGstFrom: Date | null = null;
	@observable abnStatus: string | null = null;
	@observable abnTypeCode: string | null = null;

	// ACN fields
	@observable acnClass: string | null = null;
	@observable acnName: string | null = null;
	@observable acnRegistrationDate: Date | null = null;
	@observable acnStatus: string | null = null;
	@observable acnSubClass: string | null = null;
	@observable acnTypeCode: string | null = null;
	@observable dataGovLastSynced: Date | null = null;

	@observable entityNames: EntityName[] | null = null;
	@observable public monitoredId: string | null;
	@observable public monitored: MonitoredEntity | IMonitoredEntityAttributes | null;

	constructor(attributes?: Partial<IBusinessDetailEntityAttributes>) {
		super(attributes);
	}

	@action
	public assignAttributes(attributes?: Partial<IBusinessDetailEntityAttributes>) {

		super.assignAttributes(attributes)
		if (attributes == null) return;

		// Assign all fields
		this.abnActive = attributes.abnActive ?? this.abnActive;
		this.abnLocality = attributes.abnLocality ?? this.abnLocality;
		this.abnName = attributes.abnName ?? this.abnName;
		this.abnStatus = attributes.abnStatus ?? this.abnStatus;
		this.abnTypeCode = attributes.abnTypeCode ?? this.abnTypeCode;
		this.acnClass = attributes.acnClass ?? this.acnClass;
		this.acnName = attributes.acnName ?? this.acnName;
		this.acnStatus = attributes.acnStatus ?? this.acnStatus;
		this.acnSubClass = attributes.acnSubClass ?? this.acnSubClass;
		this.acnTypeCode = attributes.acnTypeCode ?? this.acnTypeCode;

		// abnActiveSince/abnLastUpdated/abnRegisteredForGstFrom/acnRegistrationDate are calendar
		// dates (DateOnly on the backend, no time-of-day or timezone) — parsed without .utc() so
		// they land on the same calendar day regardless of the browser's timezone.
		this.abnActiveSince = attributes.abnActiveSince ? moment(attributes.abnActiveSince).toDate() : null;
		this.abnLastUpdated = attributes.abnLastUpdated ? moment(attributes.abnLastUpdated).toDate() : null;
		this.abrLastSynced = attributes.abrLastSynced ? moment.utc(attributes.abrLastSynced).toDate() : null;
		this.abnRegisteredForGstFrom = attributes.abnRegisteredForGstFrom ? moment(attributes.abnRegisteredForGstFrom).toDate() : null;
		this.acnRegistrationDate = attributes.acnRegistrationDate ? moment(attributes.acnRegistrationDate).toDate() : null;
		this.dataGovLastSynced = attributes.dataGovLastSynced ? moment.utc(attributes.dataGovLastSynced).toDate() : null;

		this.entityNames = attributes.entityNames ?? this.entityNames;

		if (attributes.monitoredId !== undefined) {
			this.monitoredId = attributes.monitoredId;
		}

		if (attributes.monitored !== undefined) {
			if (attributes.monitored === null) {
				this.monitored = attributes.monitored;
			} else if (attributes.monitored instanceof MonitoredEntity) {
				this.monitored = attributes.monitored;
				this.monitoredId = attributes.monitored.id;
			} else {
				this.monitored = new MonitoredEntity(attributes.monitored);
				this.monitoredId = this.monitored.id ?? null;
			}
		}
	}
}